import { Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import { WheelEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type AcreageAndContainmentFieldsProps = {
  isPrescribed: boolean;
};

const getFootageInAcres = (length: number, width: number): number => {
  return parseFloat(((length * width) / 43560).toFixed(2));
};

const floatFieldProps = {
  type: 'number',
  slotProps: {
    htmlInput: {
      pattern: '[0-9]+([.,][0-9]+)?',
      inputMode: 'decimal',
      onWheel: (e: WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
    },
  },
};

const intFieldProps = {
  type: 'number',
  slotProps: {
    htmlInput: {
      pattern: '[0-9]*',
      inputMode: 'numeric',
      onWheel: (e: WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
    },
  },
};

export const AcreageAndContainmentFields = (
  props: AcreageAndContainmentFieldsProps,
): JSX.Element => {
  const { isPrescribed } = props;
  const { control, formState, watch, setValue } = useFormContext();
  const { t } = useTranslation();

  const [sizeUnit, feetLength, feetWidth] = watch([
    'sizeUnit',
    'feetLength',
    'feetWidth',
  ]);
  const isUnitFeet = sizeUnit === 'feet';

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: isUnitFeet ? 4 : 12, md: 2 }}>
        <Controller
          name="sizeUnit"
          control={control}
          render={({ field }): JSX.Element => {
            const { ref, ...muiFieldProps } = field;
            return (
              <TextField
                select
                id="field-control-acreage-unit"
                label={t('createEditWildfire.inputs.acreage.unit')}
                fullWidth
                inputRef={ref}
                {...muiFieldProps}
              >
                <MenuItem value="acres">
                  {t('createEditWildfire.inputs.acreage.acres')}
                </MenuItem>
                <MenuItem value="feet">
                  {t('createEditWildfire.inputs.acreage.feet')}
                </MenuItem>
              </TextField>
            );
          }}
        />
      </Grid>

      {isUnitFeet && (
        <>
          <Grid size={{ xs: 4, md: 2 }}>
            <Controller
              name="feetLength"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, value, onChange, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-acreage-feet-length"
                    label={t('createEditWildfire.inputs.acreage.length')}
                    fullWidth
                    {...muiFieldProps}
                    value={value || ''}
                    onChange={(e) => {
                      const numVal = e.target.value
                        ? parseInt(e.target.value, 10)
                        : null;
                      onChange(numVal);
                      if (!numVal) setValue('acreage', null);
                      if (numVal && feetWidth) {
                        setValue(
                          'acreage',
                          getFootageInAcres(numVal, feetWidth),
                        );
                      }
                    }}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    disabled={formState.isSubmitting}
                    {...intFieldProps}
                  />
                );
              }}
            />
          </Grid>

          <Grid size={{ xs: 4, md: 2 }}>
            <Controller
              name="feetWidth"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, value, onChange, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-acreage-feet-width"
                    label={t('createEditWildfire.inputs.acreage.width')}
                    fullWidth
                    {...muiFieldProps}
                    value={value || ''}
                    onChange={(e) => {
                      const numVal = e.target.value
                        ? parseInt(e.target.value, 10)
                        : null;
                      onChange(numVal);
                      if (!numVal) setValue('acreage', null);
                      if (numVal && feetLength) {
                        setValue(
                          'acreage',
                          getFootageInAcres(feetLength, numVal),
                        );
                      }
                    }}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    disabled={formState.isSubmitting}
                    {...intFieldProps}
                  />
                );
              }}
            />
          </Grid>
        </>
      )}

      <Grid size={{ xs: 12, md: isUnitFeet ? 3 : 5 }}>
        <Controller
          name="acreage"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, value, onChange, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-acreage"
                label={t('createEditWildfire.inputs.acreage.label')}
                fullWidth
                {...muiFieldProps}
                value={value ?? ''}
                onChange={(e) => {
                  onChange(e.target.value ? Number(e.target.value) : null);
                  if (feetLength) setValue('feetLength', null);
                  if (feetWidth) setValue('feetWidth', null);
                }}
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={formState.isSubmitting || isUnitFeet}
                {...floatFieldProps}
                slotProps={{
                  ...floatFieldProps.slotProps,
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t('createEditWildfire.inputs.acreage.suffix')}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            );
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: isUnitFeet ? 3 : 5 }}>
        <Controller
          name="containment"
          control={control}
          render={({ field, fieldState }): JSX.Element => {
            const { ref, value, onChange, ...muiFieldProps } = field;
            return (
              <TextField
                id="field-control-containment"
                label={t('createEditWildfire.inputs.containment.label')}
                fullWidth
                {...muiFieldProps}
                value={value ?? ''}
                onChange={(e) =>
                  onChange(e.target.value ? parseInt(e.target.value, 10) : null)
                }
                inputRef={ref}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                disabled={formState.isSubmitting || isPrescribed}
                {...intFieldProps}
                slotProps={{
                  ...intFieldProps.slotProps,
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  },
                }}
              />
            );
          }}
        />
      </Grid>
    </Grid>
  );
};
